import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Download, Mail, Send, CheckCircle2, Ban, Plus, Trash2 } from 'lucide-react';

import { useApi } from '../../lib/api.js';
import { useCurrentUser } from '../../hooks/useCurrentUser.js';
import { PageHeader, LoadingPage, ErrorPage } from '../../components/ui/Feedback.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { Textarea } from '../../components/ui/Form.jsx';
import { StatementPill } from './ReportsPage.jsx';

// =============================================================================
// SalesExecStatementDetailPage — /reports/sales-exec-statements/:id
// =============================================================================
// Frozen commission statement: summary, bookings table, and actions
// (PDF, email, mark sent, mark paid, void). Mirrors the chauffeur statement
// detail page.
// =============================================================================

export function SalesExecStatementDetailPage() {
    const { id } = useParams();
    const api = useApi();
    const { isOwner, isLoading } = useCurrentUser();
    const [modal, setModal] = useState(null);   // 'email' | 'sent' | 'paid' | 'void'

    const q = useQuery({
        queryKey: ['sales-exec-statement', id],
        queryFn: () => api.get(`/sales-exec-statements/${id}`),
        enabled: isOwner && Boolean(id),
    });

    if (isLoading) return <LoadingPage />;
    if (!isOwner) return <div className="p-6 text-sm text-ink-subtle">Owner access required.</div>;
    if (q.isLoading) return <LoadingPage />;
    if (q.isError) return <ErrorPage message={q.error?.message} onRetry={q.refetch} />;

    const { statement: s, lines } = q.data;
    const canAct = s.status === 'pending' || s.status === 'sent';

    return (
        <div>
            <PageHeader
                title={
                    <span className="inline-flex items-center gap-3 flex-wrap">
                        <span>Commission statement #{s.statement_number}</span>
                        <StatementPill status={s.status} />
                    </span>
                }
                subtitle={`${s.sales_exec_name} · ${fmtDate(s.period_start)} – ${fmtDate(s.period_end)}`}
                backTo="/reports?r=sales-exec"
                backLabel="Reports"
                actions={
                    <div className="flex items-center gap-2 flex-wrap">
                        <DownloadButton statement={s} />
                        {canAct && (
                            <>
                                <Button variant="secondary" onClick={() => setModal('email')}>
                                    <Mail className="h-4 w-4" />
                                    <span className="hidden sm:inline">Email</span>
                                </Button>
                                {s.status === 'pending' && (
                                    <Button variant="secondary" onClick={() => setModal('sent')}>
                                        <Send className="h-4 w-4" />
                                        <span className="hidden sm:inline">Mark as sent</span>
                                    </Button>
                                )}
                                <Button variant="primary" onClick={() => setModal('paid')}>
                                    <CheckCircle2 className="h-4 w-4" />
                                    <span className="hidden sm:inline">Mark paid</span>
                                </Button>
                                <Button
                                    variant="secondary"
                                    className="text-status-danger hover:text-status-danger"
                                    onClick={() => setModal('void')}
                                >
                                    <Ban className="h-4 w-4" />
                                    <span className="hidden sm:inline">Void</span>
                                </Button>
                            </>
                        )}
                    </div>
                }
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                <div className="lg:col-span-1 space-y-5">
                    <div className="card">
                        <div className="card-header"><h2 className="text-sm font-semibold text-ink">Summary</h2></div>
                        <div className="card-body space-y-2 text-sm">
                            <Row label="Sales exec" value={s.sales_exec_name} />
                            {s.sales_exec_email && <Row label="Email" value={s.sales_exec_email} />}
                            <Row label="Issued" value={fmtDateTime(s.issued_at)} />
                            {s.emailed_at && <Row label="Last emailed" value={fmtDateTime(s.emailed_at)} />}
                            <div className="pt-3 mt-3 border-t border-paper-border-soft space-y-1.5">
                                <div className="flex justify-between text-ink-subtle">
                                    <span>Fares ex VAT ({s.line_count})</span>
                                    <span className="text-ink tabular-nums">{pence(s.base_total_pence)}</span>
                                </div>
                                <div className="flex justify-between font-semibold">
                                    <span>Commission due</span>
                                    <span className="tabular-nums">{pence(s.commission_total_pence)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    {s.status === 'paid' && (
                        <div className="card border-emerald-200 bg-emerald-50/40">
                            <div className="card-body text-xs text-emerald-900">
                                <div className="font-medium">Paid {fmtDateTime(s.paid_at)}</div>
                                {s.paid_note && <div className="mt-1 italic">{s.paid_note}</div>}
                            </div>
                        </div>
                    )}
                    {s.status === 'voided' && (
                        <div className="card border-amber-200 bg-amber-50/40">
                            <div className="card-body text-xs text-amber-900">
                                <div className="font-medium">Voided {fmtDateTime(s.voided_at)}</div>
                                {s.voided_reason && <div className="mt-1 italic">{s.voided_reason}</div>}
                                <div className="mt-2">Its bookings are free to go on a new statement.</div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="lg:col-span-2">
                    <div className="card">
                        <div className="card-header"><h2 className="text-sm font-semibold text-ink">Bookings</h2></div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-paper-border-soft text-xs text-ink-subtle uppercase tracking-wider">
                                        <th className="text-left px-6 py-2 font-medium">Date</th>
                                        <th className="text-left px-3 py-2 font-medium">Ref</th>
                                        <th className="text-left px-3 py-2 font-medium">Client</th>
                                        <th className="text-right px-3 py-2 font-medium">Fare ex VAT</th>
                                        <th className="text-right px-3 py-2 font-medium">Rate</th>
                                        <th className="text-right px-6 py-2 font-medium">Commission</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {lines.map((l) => (
                                        <tr key={l.id} className="border-b border-paper-border-soft last:border-b-0">
                                            <td className="px-6 py-2">{fmtDate(l.pickup_date)}</td>
                                            <td className="px-3 py-2">
                                                {l.booking_id
                                                    ? <Link to={`/bookings/${l.booking_id}`} className="font-mono text-xs hover:underline">{l.ride_ref}</Link>
                                                    : <span className="font-mono text-xs">{l.ride_ref}</span>}
                                            </td>
                                            <td className="px-3 py-2 text-ink-muted">{l.customer_name}</td>
                                            <td className="px-3 py-2 text-right tabular-nums">{pence(l.fare_ex_vat_pence)}</td>
                                            <td className="px-3 py-2 text-right tabular-nums">{Number(l.commission_pct)}%</td>
                                            <td className="px-6 py-2 text-right tabular-nums font-medium">{pence(l.commission_pence)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {modal === 'email' && <EmailModal statement={s} onClose={() => setModal(null)} onDone={() => { setModal(null); q.refetch(); }} />}
            {modal === 'sent' && (
                <SimpleActionModal
                    title="Mark as sent?"
                    text="For statements handed over or sent outside email."
                    button="Mark as sent"
                    endpoint={`/sales-exec-statements/${s.id}/mark-sent`}
                    onClose={() => setModal(null)}
                    onDone={() => { setModal(null); q.refetch(); }}
                />
            )}
            {modal === 'paid' && (
                <SimpleActionModal
                    title="Mark as paid?"
                    text="The statement is locked once paid and can no longer be voided."
                    button="Mark paid"
                    field={{ key: 'note', label: 'Note (optional)', placeholder: 'e.g. BACS ref 123456' }}
                    endpoint={`/sales-exec-statements/${s.id}/mark-paid`}
                    onClose={() => setModal(null)}
                    onDone={() => { setModal(null); q.refetch(); }}
                />
            )}
            {modal === 'void' && (
                <SimpleActionModal
                    title="Void statement?"
                    text={`The ${s.line_count} bookings on it become available for a new statement. The voided statement stays on file.`}
                    button="Void"
                    danger
                    field={{ key: 'reason', label: 'Reason', placeholder: 'e.g. Wrong period', required: true }}
                    endpoint={`/sales-exec-statements/${s.id}/void`}
                    onClose={() => setModal(null)}
                    onDone={() => { setModal(null); q.refetch(); }}
                />
            )}
        </div>
    );
}

function DownloadButton({ statement }) {
    const { getToken } = useAuth();
    const [busy, setBusy] = useState(false);
    async function download() {
        setBusy(true);
        try {
            const token = await getToken();
            const res = await fetch(`/api/sales-exec-statements/${statement.id}/pdf?download=1`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body.error || `Failed (${res.status})`);
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Lanz-CTS-Commission-${statement.statement_number}-${String(statement.sales_exec_name).replace(/[^a-zA-Z0-9-]/g, '_')}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(err.message || 'Could not download PDF');
        } finally {
            setBusy(false);
        }
    }
    return (
        <Button variant="secondary" onClick={download} loading={busy}>
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">Download PDF</span>
        </Button>
    );
}

function EmailModal({ statement, onClose, onDone }) {
    const api = useApi();
    const queryClient = useQueryClient();
    const [recipients, setRecipients] = useState(statement.sales_exec_email ? [statement.sales_exec_email] : []);
    const [draft, setDraft] = useState('');

    const mutation = useMutation({
        mutationFn: () => api.post(`/sales-exec-statements/${statement.id}/email`, { recipients }),
        onSuccess: () => {
            toast.success('Statement emailed');
            queryClient.invalidateQueries({ queryKey: ['sales-exec-statements'] });
            onDone();
        },
        onError: (err) => toast.error(err.message || 'Send failed'),
    });

    function add() {
        const v = draft.trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) { toast.error('Enter a valid email'); return; }
        if (!recipients.includes(v)) setRecipients([...recipients, v]);
        setDraft('');
    }

    return (
        <Modal open onClose={onClose} title="Email statement" description={`Statement #${statement.statement_number} is attached as a PDF.`} size="md">
            <div className="space-y-3">
                {recipients.length === 0
                    ? <p className="text-sm text-ink-subtle italic">No recipients yet.</p>
                    : (
                        <ul className="space-y-1.5">
                            {recipients.map((r) => (
                                <li key={r} className="flex items-center justify-between px-3 py-2 bg-paper-hover/50 border border-paper-border-soft rounded-md">
                                    <span className="text-sm break-all">{r}</span>
                                    <button type="button" onClick={() => setRecipients(recipients.filter((x) => x !== r))} className="text-ink-muted hover:text-status-danger">
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                <div className="flex gap-2">
                    <input
                        type="email"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
                        placeholder="email@example.com"
                        className="flex-1 px-3 py-1.5 text-sm border border-paper-border rounded bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30"
                    />
                    <Button size="sm" variant="secondary" onClick={add} disabled={!draft.trim()}>
                        <Plus className="h-3.5 w-3.5" />
                        Add
                    </Button>
                </div>
                <div className="flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                    <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
                    <Button variant="primary" onClick={() => mutation.mutate()} loading={mutation.isPending} disabled={recipients.length === 0}>
                        <Mail className="h-4 w-4" />
                        Send
                    </Button>
                </div>
            </div>
        </Modal>
    );
}

function SimpleActionModal({ title, text, button, endpoint, field, danger, onClose, onDone }) {
    const api = useApi();
    const queryClient = useQueryClient();
    const [value, setValue] = useState('');

    const mutation = useMutation({
        mutationFn: () => api.post(endpoint, field ? { [field.key]: value.trim() || null } : {}),
        onSuccess: () => {
            toast.success('Done');
            queryClient.invalidateQueries({ queryKey: ['sales-exec-statements'] });
            onDone();
        },
        onError: (err) => toast.error(err.message || 'Could not update'),
    });

    return (
        <Modal open onClose={onClose} title={title} description={text} size="md">
            {field && (
                <Textarea
                    label={field.label}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder={field.placeholder}
                    rows={3}
                />
            )}
            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
                <Button
                    variant={danger ? 'danger' : 'primary'}
                    onClick={() => mutation.mutate()}
                    loading={mutation.isPending}
                    disabled={field?.required && !value.trim()}
                >
                    {button}
                </Button>
            </div>
        </Modal>
    );
}

function Row({ label, value }) {
    return (
        <div>
            <div className="text-xs text-ink-subtle uppercase tracking-wider font-medium">{label}</div>
            <div className="text-sm text-ink mt-0.5 break-all">{value || '—'}</div>
        </div>
    );
}

function pence(p) {
    return `£${(Number(p || 0) / 100).toFixed(2)}`;
}

function fmtDate(d) {
    if (!d) return '—';
    const dt = new Date(`${String(d).slice(0, 10)}T00:00:00`);
    if (isNaN(dt.getTime())) return '—';
    return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtDateTime(d) {
    if (!d) return '—';
    return new Date(d).toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
}
